import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, X, TrendingUp, Ticket, Spade, Wallet, Gift, Trophy, MessageSquare, Home, Moon } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_MS = 20_000;
const WINDOW_MINUTES = 5;

const fmtSeenAgo = (secs) => {
    if (secs == null) return '';
    if (secs < 60) return `${secs}s ago`;
    const m = Math.floor(secs / 60);
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ago`;
};

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ACTIVITY_STYLE = {
    Casino:      { color: '#FF007F', bg: 'rgba(255,0,127,0.15)', border: 'rgba(255,0,127,0.35)' },
    Sportsbook:  { color: '#39FF14', bg: 'rgba(57,255,20,0.15)', border: 'rgba(57,255,20,0.35)' },
    Lottery:     { color: '#FFD700', bg: 'rgba(255,215,0,0.15)', border: 'rgba(255,215,0,0.35)' },
    Poker:       { color: '#00FFFF', bg: 'rgba(0,255,255,0.15)', border: 'rgba(0,255,255,0.35)' },
    Cashier:     { color: '#FFA500', bg: 'rgba(255,165,0,0.15)', border: 'rgba(255,165,0,0.35)' },
    Rewards:     { color: '#C77DFF', bg: 'rgba(199,125,255,0.15)', border: 'rgba(199,125,255,0.35)' },
    Tournaments: { color: '#FFD700', bg: 'rgba(255,215,0,0.10)', border: 'rgba(255,215,0,0.30)' },
    Referrals:   { color: '#A0AAB2', bg: 'rgba(160,170,178,0.12)', border: 'rgba(160,170,178,0.30)' },
    Support:     { color: '#00FFFF', bg: 'rgba(0,255,255,0.10)', border: 'rgba(0,255,255,0.30)' },
    Lobby:       { color: '#A0AAB2', bg: 'rgba(160,170,178,0.10)', border: 'rgba(160,170,178,0.25)' },
    Idle:        { color: '#666',    bg: 'rgba(102,102,102,0.10)', border: 'rgba(102,102,102,0.25)' },
};

const ActivityChip = ({ activity }) => {
    const s = ACTIVITY_STYLE[activity] || ACTIVITY_STYLE.Lobby;
    return (
        <span
            className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider shrink-0"
            style={{ color: s.color, background: s.bg, border: `1px solid ${s.border}` }}
        >
            {activity || 'Lobby'}
        </span>
    );
};

const ACTIVITY_ICON = {
    Casino: Spade,
    Sportsbook: TrendingUp,
    Lottery: Ticket,
    Poker: Spade,
    Cashier: Wallet,
    Rewards: Gift,
    Tournaments: Trophy,
    Support: MessageSquare,
    Referrals: Users,
    Lobby: Home,
    Idle: Moon,
};

// Order activity sections by where the "money" / engagement matters most
const ROLLUP_ORDER = [
    'Slots', 'Sportsbook', 'Lottery', 'Poker',
    'Cashier', 'Tournaments', 'Rewards', 'Referrals', 'Support',
    'Lobby', 'Idle',
];

const buildRollup = (players) => {
    const counts = {};
    players.forEach((p) => {
        const k = p.activity || 'Lobby';
        counts[k] = (counts[k] || 0) + 1;
    });
    return ROLLUP_ORDER.filter((k) => counts[k]).map((k) => ({ key: k, count: counts[k] }));
};

/**
 * Admin-only "Active Players Now" widget.
 * Shows a pulsing live-dot + count of users seen in the last 5 minutes.
 * Click to expand a popover with the live player list.
 * NEVER mounted on the player side.
 */
const ActivePlayersNowWidget = () => {
    const [data, setData] = useState({ count: 0, players: [] });
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data: res } = await axios.get(
                    `${API}/admin/active-players?minutes=${WINDOW_MINUTES}`,
                    { withCredentials: true },
                );
                if (!cancelled) setData({ count: res?.count || 0, players: res?.players || [] });
            } catch { /* ignore - non-admin will 403 */ }
        };
        load();
        const id = setInterval(load, POLL_MS);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    // Close popover on outside click
    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className="relative" ref={wrapRef}>
            <button
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-2 px-3 h-9 rounded-full border border-[#39FF14]/30 bg-[#39FF14]/5 hover:bg-[#39FF14]/10 transition-colors"
                data-testid="active-players-widget"
                title={`${data.count} player${data.count === 1 ? '' : 's'} active (last ${WINDOW_MINUTES} min)`}
            >
                <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-[#39FF14] opacity-70 animate-ping"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#39FF14]"></span>
                </span>
                <Users className="w-3.5 h-3.5 text-[#39FF14]" />
                <span className="text-xs font-black text-[#39FF14] tabular-nums" data-testid="active-players-count">
                    {data.count}
                </span>
                <span className="text-[10px] text-[#39FF14]/70 font-bold tracking-wider hidden sm:inline">LIVE</span>
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full mt-2 w-80 max-h-[420px] bg-[#0a0a0a] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col"
                        data-testid="active-players-popover"
                    >
                        <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between bg-[#39FF14]/5 shrink-0">
                            <div className="flex items-center gap-2">
                                <span className="relative flex h-2 w-2">
                                    <span className="absolute inline-flex h-full w-full rounded-full bg-[#39FF14] opacity-70 animate-ping"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#39FF14]"></span>
                                </span>
                                <h4 className="text-sm font-bold text-white">
                                    Active Now <span className="text-[#39FF14]">({data.count})</span>
                                </h4>
                                <span className="text-[10px] text-[#A0AAB2]">last {WINDOW_MINUTES}m</span>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                className="text-[#A0AAB2] hover:text-white p-1"
                                aria-label="Close"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        {/* Activity rollup — "where is the floor right now" */}
                        {data.players.length > 0 && (() => {
                            const rollup = buildRollup(data.players);
                            return (
                                <div className="px-2 py-1.5 border-b border-white/10 bg-white/[0.02] flex items-center gap-1 overflow-x-auto shrink-0" data-testid="active-players-rollup">
                                    {rollup.map(({ key, count }) => {
                                        const s = ACTIVITY_STYLE[key] || ACTIVITY_STYLE.Lobby;
                                        const Icon = ACTIVITY_ICON[key] || Home;
                                        return (
                                            <div
                                                key={key}
                                                className="flex items-center gap-1 px-1.5 py-0.5 rounded shrink-0"
                                                style={{ background: s.bg, border: `1px solid ${s.border}` }}
                                                data-testid={`rollup-${key.toLowerCase()}`}
                                                title={`${count} in ${key}`}
                                            >
                                                <Icon className="w-3 h-3" style={{ color: s.color }} />
                                                <span className="text-[10px] font-black tabular-nums" style={{ color: s.color }}>{count}</span>
                                                <span className="text-[9px] font-semibold uppercase tracking-wider hidden md:inline" style={{ color: s.color }}>{key}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}

                        <div className="overflow-y-auto flex-1 min-h-0">
                            {data.players.length === 0 ? (
                                <div className="px-3 py-8 text-center text-[#A0AAB2] text-xs">
                                    No players online right now.
                                </div>
                            ) : (
                                data.players.map((p) => (
                                    <div
                                        key={p.id}
                                        className="px-3 py-2.5 border-b border-white/5 flex items-center gap-2 hover:bg-white/[0.02]"
                                        data-testid={`active-player-${p.id}`}
                                    >
                                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#39FF14]/40 to-[#FFD700]/40 flex items-center justify-center text-[10px] font-black text-white shrink-0">
                                            {(p.name || '?').slice(0, 2).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <p className="text-xs font-semibold text-white truncate max-w-[120px]">{p.name}</p>
                                                <ActivityChip activity={p.activity} />
                                                {p.role !== 'user' && (
                                                    <span className="text-[9px] px-1 py-0.5 rounded bg-[#FFD700]/15 text-[#FFD700] font-bold uppercase">{p.role}</span>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-[#A0AAB2] truncate">{p.email}</p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="text-[10px] text-[#39FF14] font-bold">{fmtSeenAgo(p.seconds_ago)}</p>
                                            <p className="text-[10px] text-[#A0AAB2] tabular-nums">{fmt$(p.credits)}</p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ActivePlayersNowWidget;
